import { useCallback, useMemo } from 'react'
import {
    ActionEventDocument,
    ChannelEventDocument,
    CombinedResult,
    DmChannelEventDocument,
    EventDocument,
    isCombinedResultItem,
    SpaceEventDocument,
    UserEventDocument,
} from '@components/SearchBar/types'
import { useIndexMessages } from 'hooks/useIndexMessages'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { firstBy } from 'thenby'
import { notUndefined } from 'ui/utils/utils'
import {
    LookupUser,
    useSpaceId,
    useSpaceMembersWithFallback,
    useTownsContext,
    useUserLookupContext,
} from 'use-towns-client'
import { lookupUserNameSearchString } from 'utils/lookupUserNameSearchString'

import { useFeatureFlags } from './useFeatureFlags'
import { useMiniSearch } from './useMiniSearch'
import { useSortedChannels } from './useSortedChannels'

export const useSearch = (searchTerms: string) => {
    const spaceId = useSpaceId()
    const { enableBotDm } = useFeatureFlags()
    const { memberIds } = useSpaceMembersWithFallback(spaceId, { excludeBots: !enableBotDm })
    const { lookupUser } = useUserLookupContext()

    const users = useMemo(
        () => memberIds.map((m) => lookupUser(m)).filter(notUndefined),
        [lookupUser, memberIds],
    )

    const { spaces } = useTownsContext()
    const { messages: indexedMessages, dmMessages: indexedDMMessages } = useIndexMessages()

    const { channelItems, dmItems } = useSortedChannels({ spaceId: '' })
    const { closePanel, openPanel, isPanelOpen } = usePanelActions()

    const indexedSpaces = useMemo<SpaceEventDocument[]>(
        () =>
            spaces
                .filter((s) => s.id !== spaceId)
                .map((s) => ({
                    key: `space-${s.id}`,
                    type: 'space' as const,
                    body: s.name,
                    source: s,
                })),
        [spaces, spaceId],
    )

    const indexedChannels = useMemo<ChannelEventDocument[]>(
        () =>
            channelItems.map((c) => ({
                key: `channel-${c.id}`,
                type: 'channel' as const,
                body: c.label + c.search,
                source: {
                    ...c,
                    isJoined: true,
                    muted: false,
                },
            })),
        [channelItems],
    )
    const indexedDmChannels = useMemo<DmChannelEventDocument[]>(
        () =>
            dmItems
                .filter((c) => c.isGroup)
                .map((c) => ({
                    key: `dm-channel-${c.id}`,
                    type: 'dmChannel' as const,
                    body: c.label + c.search,
                    source: {
                        ...c,
                    },
                })),
        [dmItems],
    )

    const indexedMembers = useMemo<UserEventDocument[]>(
        () =>
            users.map((u: LookupUser) => ({
                key: `user-${u.userId}`,
                type: 'user' as const,
                body: lookupUserNameSearchString(u),
                source: u,
            })),
        [users],
    )

    const openBugReport = useCallback(() => {
        if (isPanelOpen('bug-report')) {
            closePanel()
        } else {
            openPanel('bug-report')
        }
    }, [closePanel, isPanelOpen, openPanel])

    const indexedActions = useMemo<ActionEventDocument[]>(
        () => [
            {
                key: `actionreport`,
                type: 'action' as const,
                body: 'Report Bug, Feedback, Error, Issue',
                source: {
                    icon: 'bug',
                    label: 'Report Bug',
                    callback: openBugReport,
                },
            },
        ],
        [openBugReport],
    )

    const searchItems = useMemo(
        () => [
            ...indexedSpaces,
            ...indexedMembers,
            ...indexedChannels,
            ...indexedDmChannels,
            ...indexedMessages,
            ...indexedDMMessages,
            ...indexedActions,
        ],
        [
            indexedSpaces,
            indexedMembers,
            indexedChannels,
            indexedDmChannels,
            indexedMessages,
            indexedDMMessages,
            indexedActions,
        ],
    )

    const order = ['user', 'dmChannel', 'channel', 'dmMessage', 'message']

    const miniSearchResults = useMiniSearch(searchItems, searchTerms)

    const searchResults = miniSearchResults
        .map((r) => ({ searchResult: r, item: searchItems.find((i) => i.key === r.id) }))
        .filter(isCombinedResultItem)
        .slice(0, 50)
        .sort(
            firstBy<CombinedResult>((r) => {
                // Prioritize exact substring matches
                const text = getSearchableText(r.item)
                const containsExact = text.toLowerCase().includes(searchTerms.toLowerCase())
                return containsExact ? -1 : 0
            })
                .thenBy<CombinedResult>((r) => {
                    // Then prioritize prefix matches
                    const text = getSearchableText(r.item)
                    const isPrefix = text.toLowerCase().startsWith(searchTerms.toLowerCase())
                    return isPrefix ? -1 : 0
                })
                .thenBy<CombinedResult>((r) => order.indexOf(r.item.type))
                .thenBy<CombinedResult>((r) => -r.searchResult.score),
        )

    return { searchResults }
}

function getSearchableText(item: EventDocument): string {
    switch (item.type) {
        case 'user':
            return lookupUserNameSearchString(item.source)
        case 'channel':
        case 'dmChannel':
            return item.source.label || ''
        case 'message':
        case 'dmMessage':
            return item.body || ''
        case 'action':
            return item.source.label || ''
        case 'space':
            return item.source.name || ''
        default:
            return ''
    }
}
